import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';

import { Loading } from '@autopass-web/mochi-component';

import { ENV_CONFIG, PATH } from '../../config';
import Client from '../../services/client';
import { useBootStraps } from '../../services/use-bootstraps';
import { useUserMe } from '../../services/use-user-me';

interface GlikeWrapper {
  children: React.ReactNode;
}

const useCheckIfBindingIsSucceeded = () => {
  const [isChecking, setIsChecking] = useState(false);
  const [willRedirect, setWillRedirect] = useState(false);
  const queryClient = useQueryClient();
  const { asPath, replace, query, push } = useRouter();

  useEffect(() => {
    const checkBinding = async () => {
      setWillRedirect(false);
      const hashPart = asPath.split('#')[1];
      const hash = new URLSearchParams(hashPart);
      if (!hashPart) return;

      const result = hash.get('result');

      if (result !== 'success') {
        return;
      }

      const token = hash.get('token');
      const transactionId = hash.get('merchant_transaction_id');

      try {
        setIsChecking(true);
        await Client.createTransactionToken({
          token,
          transactionId,
        });

        queryClient.invalidateQueries({ queryKey: ['me'] });

        const nextQuery = query.uid ? { uid: query.uid } : {};

        setWillRedirect(true);

        window.location.href = `${ENV_CONFIG.GLIKE_LINK}?${qs.stringify(nextQuery)}`;
      } catch (err) {
        console.log(err);
      } finally {
        setIsChecking(false);
      }
    };

    checkBinding();
  }, [asPath, push, query.uid, queryClient, replace]);

  return { isChecking, willRedirect };
};

const ALLOWED_PATHS = [
  PATH.USER_BIND_CARD,
  PATH.AUTH,
  PATH.MAIN,
  PATH.EXPIRED,
].filter((p) => p !== null);

const AUTH_REQUIRED_PATHS = ALLOWED_PATHS.filter(
  (p) => !(p === PATH.AUTH || p === PATH.EXPIRED),
);

const checkIsAllowedPath = (path: string) => {
  return ALLOWED_PATHS.some((p) => path.includes(p)) || path == PATH.EXPIRED;
};

export const checkIsAuthRequiredPath = (path: string) =>
  AUTH_REQUIRED_PATHS.some((p) => path.includes(p));

const useRoutesGateway = () => {
  const { userMe, isGetUserMeLoading, refetchUser } = useUserMe({
    retry: false,
  });
  const { asPath, replace, push, query, pathname } = useRouter();

  const [checked, setIsChecked] = useState(false);

  useEffect(() => {
    function getNavigateToShouldBe() {
      if (isGetUserMeLoading) return;

      if (!checkIsAllowedPath(pathname)) {
        return async () => {
          await replace({ pathname: PATH.EXPIRED, query });
        };
      }

      if (!checkIsAuthRequiredPath(pathname)) {
        return undefined;
      }

      if (pathname.includes(PATH.AUTH)) {
        return undefined;
      }

      const oneTimeToken = query.OneTimeToken as string | undefined;
      if (oneTimeToken) {
        return async () => {
          await push({
            pathname: PATH.AUTH,
            query: {
              ...query,
              prevPath: pathname,
            },
          });
        };
      }

      if (!userMe) {
        return async () => {
          try {
            await refetchUser();
          } catch {
            await push({
              pathname: PATH.AUTH,
              query: {
                ...query,
                prevPath: pathname,
              },
            });
          }
        };
      }
    }

    const navigateToBe = async () => {
      setIsChecked(false);
      if (isGetUserMeLoading) return;

      const toShouldBe = getNavigateToShouldBe();
      if (toShouldBe) {
        await toShouldBe();
      }
      setIsChecked(true);
    };

    navigateToBe();
  }, [
    asPath,
    push,
    replace,
    query,
    pathname,
    isGetUserMeLoading,
    userMe,
    refetchUser,
  ]);

  return { checked };
};

export const GlikeWrapper = ({ children }: GlikeWrapper) => {
  useBootStraps();
  const { isGetUserMeLoading } = useUserMe({ enabled: false, retry: false });

  const { checked } = useRoutesGateway();
  const { isChecking, willRedirect } = useCheckIfBindingIsSucceeded();

  if (isGetUserMeLoading || isChecking || !checked || willRedirect) {
    return <Loading />;
  }

  return children;
};
